/**
 * @file ProductSeoSection.tsx
 * @description «Для поиска» карточки товара (phase19.16, партия Р3в,
 * v0.19.64; SEO11, SEO12): главный и дополнительные запросы — заметка
 * владельца, на сайт не выводится. Сведения: главного запроса нет в
 * заголовке для поиска (свой заголовок или название товара) и «этот запрос
 * уже у …» (ответ сервера). Сохраняется общей кнопкой «Сохранить» карточки.
 * Свёрнута по умолчанию — поле нужно не каждый раз.
 *
 * «SEO-техника» v0.19.65 (TD-634): поле «Заголовок для поиска» (`metaTitle`)
 * — было в модели, но не в форме. Пусто — название товара и окончание
 * заголовков из «Сайт → SEO»; подсказка о ~60 знаках, которые покажет поиск.
 */

import { Search } from "lucide-react";

import { SectionCard } from "./SectionCard";
import { SeoKeywordsField } from "../../../../components/site-seo/SeoKeywordsField";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import type { CatalogProduct } from "../../../../lib/api-catalog";
import { PAGE_SEO_TITLE_MAX, PAGE_SEO_TITLE_SOFT } from "../../../../lib/site-builder/page-form";
import type { ProductFormState } from "../product-form";

interface Props {
  form: ProductFormState;
  product: CatalogProduct | null;
  onChange: (patch: Partial<ProductFormState>) => void;
  disabled?: boolean;
}

export function ProductSeoSection({ form, product, onChange, disabled }: Props) {
  const filled = form.metaTitle.trim() !== "" || form.keywords.main.trim() !== "" || form.keywords.extra.length > 0;
  const length = form.metaTitle.trim().length;
  return (
    <SectionCard icon={<Search />} title="Для поиска" collapsible defaultExpanded={filled}>
      <div className="flex flex-col gap-4">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Label htmlFor="p-meta-title">Заголовок для поиска</Label>
          <Input
            id="p-meta-title"
            value={form.metaTitle}
            placeholder={form.displayName}
            maxLength={PAGE_SEO_TITLE_MAX}
            onChange={(e) => onChange({ metaTitle: e.target.value })}
            disabled={disabled}
          />
          <p className={length > PAGE_SEO_TITLE_SOFT ? "type-caption text-warning" : "type-caption text-on-surface-variant"}>
            {length > PAGE_SEO_TITLE_SOFT
              ? `${length} знаков — поиск покажет первые ~${PAGE_SEO_TITLE_SOFT}.`
              : "Пусто — название товара и окончание заголовков из «Сайт → SEO»."}
          </p>
        </div>
        <SeoKeywordsField
          value={form.keywords}
          onChange={(keywords) => onChange({ keywords })}
          title={form.metaTitle.trim() || form.displayName}
          h1={form.displayName}
          conflicts={product?.seoKeywordConflicts ?? []}
          disabled={disabled}
        />
      </div>
    </SectionCard>
  );
}
